import React, { useEffect } from 'react';
import { Button, Form, Input, InputNumber, Modal, Popconfirm, Select, notification } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import {
	ILocationDiagram,
	IShelfWithLocations,
	useCreateLocationMutation,
	useDeleteLocationMutation,
	useUpdateLocationMutation,
} from '../../../../redux/api/inventory';
import { SLOT_TYPES, VOLUME_TYPES } from '../../constants/inventory';

interface Props {
	open: boolean;
	shelfId?: string;
	/** The rack's storage type — drives which capacity fields are shown. */
	storageType: string;
	/** Shelves to choose from when adding without a preselected shelf. */
	shelves?: IShelfWithLocations[];
	location?: ILocationDiagram | null;
	/** Pre-filled code when creating from an empty grid cell (e.g. "B7"). */
	prefillCode?: string;
	onClose: () => void;
}

const LocationFormModal: React.FC<Props> = ({
	open,
	shelfId,
	storageType,
	shelves,
	location,
	prefillCode,
	onClose,
}) => {
	const [form] = Form.useForm();
	const isEdit = !!location;
	// When adding without a fixed shelf (header button), let the user pick one.
	const needsShelfPicker = !isEdit && !shelfId;

	const [createLocation, { isLoading: creating }] = useCreateLocationMutation();
	const [updateLocation, { isLoading: updating }] = useUpdateLocationMutation();
	const [deleteLocation, { isLoading: deleting }] = useDeleteLocationMutation();

	const isSlots = SLOT_TYPES.includes(storageType);
	const isBins = VOLUME_TYPES.includes(storageType);

	useEffect(() => {
		if (!open) return;
		if (location) {
			form.setFieldsValue({
				code: location.code,
				description: location.description,
				max_volume: location.max_volume,
				max_width: location.max_width,
				max_height: location.max_height,
			});
		} else {
			form.resetFields();
			form.setFieldsValue({
				...(isBins ? { max_volume: 675 } : {}),
				...(prefillCode ? { code: prefillCode } : {}),
			});
		}
	}, [open, location, form, isBins, prefillCode]);

	const handleOk = async () => {
		const values = await form.validateFields();
		const body = {
			type: storageType,
			code: values.code,
			description: values.description ?? '',
			max_volume: isBins ? values.max_volume ?? null : null,
			max_width: isSlots ? values.max_width ?? null : null,
			max_height: isSlots ? values.max_height ?? null : null,
		};
		try {
			if (isEdit && location) {
				await updateLocation({ id: location.id, body }).unwrap();
				notification.success({ message: `Location ${values.code} updated` });
			} else {
				const targetShelf = shelfId ?? values.shelf;
				await createLocation({ shelf: targetShelf, ...body }).unwrap();
				notification.success({ message: `Location ${values.code} created` });
			}
			onClose();
		} catch (err: any) {
			notification.error({
				message: 'Could not save location',
				description: err?.data?.detail || 'Please review the form and try again.',
			});
		}
	};

	const handleDelete = async () => {
		if (!location) return;
		try {
			await deleteLocation(location.id).unwrap();
			notification.success({ message: `Location ${location.code} deleted` });
			onClose();
		} catch (err: any) {
			notification.error({
				message: 'Could not delete location',
				description: err?.data?.detail || 'This location could not be deleted.',
			});
		}
	};

	return (
		<Modal
			title={isEdit ? `Edit Location ${location?.code}` : 'Add Location'}
			open={open}
			onCancel={onClose}
			onOk={handleOk}
			confirmLoading={creating || updating}
			okText={isEdit ? 'Save' : 'Create'}
			destroyOnClose
			footer={[
				isEdit && (
					<Popconfirm
						key="delete"
						title="Delete this location?"
						onConfirm={handleDelete}
					>
						<Button danger icon={<DeleteOutlined />} loading={deleting} style={{ float: 'left' }}>
							Delete
						</Button>
					</Popconfirm>
				),
				<Button key="cancel" onClick={onClose}>
					Cancel
				</Button>,
				<Button
					key="ok"
					type="primary"
					loading={creating || updating}
					onClick={handleOk}
				>
					{isEdit ? 'Save' : 'Create'}
				</Button>,
			]}
		>
			<Form form={form} layout="vertical">
				{needsShelfPicker && (
					<Form.Item
						name="shelf"
						label="Shelf"
						rules={[{ required: true, message: 'Select a shelf' }]}
					>
						<Select
							placeholder="Select shelf"
							options={(shelves || []).map((s) => ({
								label: s.name,
								value: s.id,
							}))}
						/>
					</Form.Item>
				)}

				<Form.Item
					name="code"
					label="Location Code"
					rules={[{ required: true, message: 'Location code is required' }]}
				>
					<Input maxLength={20} placeholder="e.g. A1" />
				</Form.Item>

				{isBins && (
					<Form.Item name="max_volume" label="Max Volume (in³)">
						<InputNumber style={{ width: '100%' }} min={0} />
					</Form.Item>
				)}

				{isSlots && (
					<>
						<Form.Item name="max_width" label="Max Width (in)">
							<InputNumber style={{ width: '100%' }} min={0} step={0.25} />
						</Form.Item>
						<Form.Item name="max_height" label="Max Height (in)">
							<InputNumber style={{ width: '100%' }} min={0} step={0.25} />
						</Form.Item>
					</>
				)}

				<Form.Item name="description" label="Description">
					<Input.TextArea rows={2} />
				</Form.Item>
			</Form>
		</Modal>
	);
};

export default LocationFormModal;
